import { templateById, templates } from "./templates";
import { accessoryDefinitions } from "./accessories";
import type {
  AccessoryId,
  DecorationSlot,
  EditSettings,
  PreparedCard,
  PreparedProject,
  Ratio,
  Transform,
} from "../types";

const assetCache = new Map<string, Promise<HTMLImageElement>>();
const resolvedAssets = new Map<string, HTMLImageElement>();
const visibleBoundsCache = new WeakMap<HTMLImageElement, SourceBounds>();
const spriteBoundsCache = new Map<Exclude<AccessoryId, "none">, SourceBounds>();

type SourceBounds = {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
};

export const outputSizes: Record<Ratio, { width: number; height: number }> = {
  "4:5": { width: 1080, height: 1350 },
  "1:1": { width: 1080, height: 1080 },
  "9:16": { width: 1080, height: 1920 },
};

function loadAsset(url: string) {
  const existing = assetCache.get(url);
  if (existing) return existing;
  const request = new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => {
      resolvedAssets.set(url, image);
      resolve(image);
    };
    image.onerror = () => {
      assetCache.delete(url);
      reject(new Error(`素材を読み込めませんでした: ${url}`));
    };
    image.src = url;
  });
  assetCache.set(url, request);
  return request;
}

function highQuality(context: CanvasRenderingContext2D) {
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
}

function visibleBounds(image: HTMLImageElement): SourceBounds {
  const cached = visibleBoundsCache.get(image);
  if (cached) return cached;
  const naturalWidth = image.naturalWidth;
  const naturalHeight = image.naturalHeight;
  const sampleScale = Math.min(1, 512 / naturalWidth, 256 / naturalHeight);
  const sampleWidth = Math.max(1, Math.round(naturalWidth * sampleScale));
  const sampleHeight = Math.max(1, Math.round(naturalHeight * sampleScale));
  const sample = document.createElement("canvas");
  sample.width = sampleWidth;
  sample.height = sampleHeight;
  const context = sample.getContext("2d", { willReadFrequently: true });
  if (!context) return { sx: 0, sy: 0, sw: naturalWidth, sh: naturalHeight };
  highQuality(context);
  context.drawImage(image, 0, 0, sampleWidth, sampleHeight);
  const pixels = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
  let left = sampleWidth;
  let top = sampleHeight;
  let right = 0;
  let bottom = 0;
  for (let y = 0; y < sampleHeight; y += 1) {
    for (let x = 0; x < sampleWidth; x += 1) {
      if (pixels[(y * sampleWidth + x) * 4 + 3] < 18) continue;
      left = Math.min(left, x);
      top = Math.min(top, y);
      right = Math.max(right, x + 1);
      bottom = Math.max(bottom, y + 1);
    }
  }
  if (right <= left || bottom <= top) {
    const fallback = { sx: 0, sy: 0, sw: naturalWidth, sh: naturalHeight };
    visibleBoundsCache.set(image, fallback);
    return fallback;
  }
  const padding = 3;
  left = Math.max(0, left - padding);
  top = Math.max(0, top - padding);
  right = Math.min(sampleWidth, right + padding);
  bottom = Math.min(sampleHeight, bottom + padding);
  const scaleX = naturalWidth / sampleWidth;
  const scaleY = naturalHeight / sampleHeight;
  const bounds = {
    sx: Math.floor(left * scaleX),
    sy: Math.floor(top * scaleY),
    sw: Math.ceil((right - left) * scaleX),
    sh: Math.ceil((bottom - top) * scaleY),
  };
  visibleBoundsCache.set(image, bounds);
  return bounds;
}

export async function ensureTemplateAssets(templateId: EditSettings["template"]) {
  const template = templateById(templateId);
  await Promise.all([
    loadAsset(template.paper).catch(() => null),
    loadAsset(template.privacy).catch(() => null),
    template.frame ? loadAsset(template.frame).catch(() => null) : Promise.resolve(null),
  ]);
}

export async function ensureAccessoryAssets(ids: readonly AccessoryId[]) {
  const urls = new Set(
    ids
      .filter((id): id is Exclude<AccessoryId, "none"> => id !== "none")
      .map((id) => accessoryDefinitions[id].atlas),
  );
  await Promise.all([...urls].map((url) => loadAsset(url).catch(() => null)));
}

export function selectedDecorationIds(settings: EditSettings) {
  return Object.values(settings.decorations).flat();
}

export function warmTemplateAssets(current: EditSettings["template"]) {
  void ensureTemplateAssets(current);
  const currentIndex = templates.findIndex((template) => template.id === current);
  const nearby = templates
    .filter((template) => template.id !== current)
    .sort((left, right) => {
      const leftDistance = Math.abs(templates.indexOf(left) - currentIndex);
      const rightDistance = Math.abs(templates.indexOf(right) - currentIndex);
      return leftDistance - rightDistance;
    })
    .slice(0, 3);
  const run = () => {
    const connection = (
      navigator as Navigator & {
        connection?: { saveData?: boolean };
      }
    ).connection;
    if (connection?.saveData) return;
    for (const template of nearby) {
      void loadAsset(template.paper).catch(() => null);
      void loadAsset(template.privacy).catch(() => null);
    }
  };
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 5000 });
  } else {
    globalThis.setTimeout(run, 1500);
  }
}

function roundedPath(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const r = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + width, y, x + width, y + height, r);
  context.arcTo(x + width, y + height, x, y + height, r);
  context.arcTo(x, y + height, x, y, r);
  context.arcTo(x, y, x + width, y, r);
  context.closePath();
}

function drawCover(
  context: CanvasRenderingContext2D,
  image: CanvasImageSource,
  imageWidth: number,
  imageHeight: number,
  width: number,
  height: number,
) {
  const scale = Math.max(width / imageWidth, height / imageHeight);
  const drawWidth = imageWidth * scale;
  const drawHeight = imageHeight * scale;
  context.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
}

function drawSparkle(context: CanvasRenderingContext2D, x: number, y: number, size: number, color: string) {
  context.save();
  context.translate(x, y);
  context.fillStyle = color;
  context.beginPath();
  context.moveTo(0, -size);
  context.quadraticCurveTo(size * 0.12, -size * 0.12, size, 0);
  context.quadraticCurveTo(size * 0.12, size * 0.12, 0, size);
  context.quadraticCurveTo(-size * 0.12, size * 0.12, -size, 0);
  context.quadraticCurveTo(-size * 0.12, -size * 0.12, 0, -size);
  context.fill();
  context.restore();
}

function hash(seed: number) {
  let value = seed + 0x6d2b79f5;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
}

function drawBackground(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  settings: EditSettings,
) {
  const template = templateById(settings.template);
  context.fillStyle = template.surface;
  context.fillRect(0, 0, width, height);
  const paper = resolvedAssets.get(template.paper);
  if (paper) {
    drawCover(context, paper, paper.naturalWidth, paper.naturalHeight, width, height);
  } else {
    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#fff6fa");
    gradient.addColorStop(0.5, "#fffdf8");
    gradient.addColorStop(1, "#eefaf8");
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
  }
  context.globalAlpha = 0.58;
  for (let index = 0; index < 9; index += 1) {
    const x = 42 + hash(settings.seed + index * 13) * (width - 84);
    const y = 42 + hash(settings.seed + index * 29) * (height - 84);
    drawSparkle(context, x, y, 5 + hash(settings.seed + index * 41) * 8, index % 2 ? "#fff" : template.ink);
  }
  context.globalAlpha = 1;
}

function drawPrivacy(
  context: CanvasRenderingContext2D,
  card: PreparedCard,
  cardWidth: number,
  cardHeight: number,
  templateId: EditSettings["template"],
) {
  const template = templateById(templateId);
  const image = resolvedAssets.get(template.privacy);
  const box = card.nameBox;
  const left = Math.max(cardWidth * 0.235, box.left * cardWidth);
  const top = Math.max(cardHeight * 0.035, (box.top - 0.02) * cardHeight);
  const targetWidth = Math.min(cardWidth * 0.49, Math.max(cardWidth * 0.22, (box.right - box.left + 0.08) * cardWidth));
  const targetHeight = Math.max(cardHeight * 0.22, (box.bottom - box.top + 0.07) * cardHeight);
  if (!image) {
    context.save();
    context.fillStyle = "rgba(248,248,247,.96)";
    roundedPath(context, left, top, targetWidth, targetHeight, targetHeight / 2);
    context.fill();
    context.restore();
    return;
  }
  const source = visibleBounds(image);
  const visibleRatio = source.sw / source.sh;
  // Generated masks use canvases with very different amounts of transparent
  // padding. Size the actual painted object, not the PNG canvas.
  let width = Math.min(
    cardWidth * 0.53,
    Math.max(cardWidth * 0.29, targetWidth),
  );
  let height = width / visibleRatio;
  const maxHeight = cardHeight * 0.36;
  if (height > maxHeight) {
    height = maxHeight;
    width = height * visibleRatio;
  }
  const centerY = (box.top + box.bottom) * cardHeight / 2;
  const drawTop = Math.max(
    cardHeight * 0.01,
    Math.min(cardHeight - height - cardHeight * 0.01, centerY - height / 2),
  );
  context.drawImage(
    image,
    source.sx,
    source.sy,
    source.sw,
    source.sh,
    left - width * 0.025,
    drawTop,
    width,
    height,
  );
}

export function heroGeometry(
  card: PreparedCard,
  cardWidth: number,
  cardHeight: number,
  composition: EditSettings["composition"],
  adjustment: Transform,
) {
  const heightFactor = composition === "bold" ? 1.34 : composition === "flat" ? 0.98 : 1.18;
  const widthLimit = composition === "bold" ? 0.66 : composition === "flat" ? 0.48 : 0.57;
  const baseScale = Math.min(
    cardHeight * heightFactor / card.hero.height,
    cardWidth * widthLimit / card.hero.width,
  );
  const scale = baseScale * adjustment.scale;
  const width = card.hero.width * scale;
  const height = card.hero.height * scale;
  const targetHeadX = cardWidth * (composition === "flat" ? 0.83 : 0.86);
  const anchoredX = targetHeadX - card.heroHead.x * width;
  const x = Math.min(
    cardWidth * 1.035 - width,
    Math.max(cardWidth * 0.37, anchoredX),
  ) + adjustment.x * cardWidth;
  const y = cardHeight - height + adjustment.y * cardHeight;
  return { x, y, width, height };
}

function drawDecoration(
  context: CanvasRenderingContext2D,
  id: AccessoryId,
  card: PreparedCard,
  hero: ReturnType<typeof heroGeometry>,
  adjustment: Transform,
  cardWidth: number,
  cardHeight: number,
  slot: DecorationSlot,
) {
  if (id === "none") return;
  const definition = accessoryDefinitions[id];
  if (definition.slot !== slot) return;
  const atlas = resolvedAssets.get(definition.atlas);
  if (!atlas) return;
  const bounds = accessorySourceBounds(atlas, id, definition.source);
  const ratio = bounds.sw / bounds.sh;
  let width: number;
  let height: number;
  if (definition.width) {
    width = cardWidth * definition.width * adjustment.scale;
    height = width / ratio;
  } else {
    height = cardHeight * definition.height * adjustment.scale;
    width = height * ratio;
  }
  let anchorX = hero.x + card.heroHead.x * hero.width;
  let anchorY = hero.y + card.heroHead.y * hero.height;
  if (definition.anchor === "hero") {
    anchorX = hero.x + hero.width * 0.55;
    anchorY = hero.y + hero.height * 0.48;
  } else if (definition.anchor === "card") {
    anchorX = cardWidth / 2;
    anchorY = cardHeight / 2;
  }
  anchorX += (definition.offsetX ?? 0) * cardHeight + adjustment.x * cardWidth;
  anchorY += (definition.offsetY ?? 0) * cardHeight + adjustment.y * cardHeight;
  context.save();
  context.globalAlpha = definition.opacity ?? 1;
  context.translate(anchorX, anchorY);
  context.rotate(adjustment.angle * Math.PI / 180);
  context.scale(adjustment.flipX ? -1 : 1, 1);
  context.drawImage(
    atlas,
    bounds.sx,
    bounds.sy,
    bounds.sw,
    bounds.sh,
    -width / 2,
    -height * definition.lift,
    width,
    height,
  );
  context.restore();
}

function accessorySourceBounds(
  image: HTMLImageElement,
  id: Exclude<AccessoryId, "none">,
  source: readonly [number, number, number, number],
): SourceBounds {
  const cached = spriteBoundsCache.get(id);
  if (cached) return cached;
  const [sx, sy, sw, sh] = source;
  const sample = document.createElement("canvas");
  sample.width = sw;
  sample.height = sh;
  const context = sample.getContext("2d", { willReadFrequently: true });
  if (!context) return { sx, sy, sw, sh };
  context.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh);
  const pixels = context.getImageData(0, 0, sw, sh).data;
  let left = sw;
  let top = sh;
  let right = 0;
  let bottom = 0;
  for (let y = 0; y < sh; y += 1) {
    for (let x = 0; x < sw; x += 1) {
      if (pixels[(y * sw + x) * 4 + 3] < 18) continue;
      left = Math.min(left, x);
      top = Math.min(top, y);
      right = Math.max(right, x + 1);
      bottom = Math.max(bottom, y + 1);
    }
  }
  if (right <= left || bottom <= top) return { sx, sy, sw, sh };
  const padding = 3;
  const bounds = {
    sx: sx + Math.max(0, left - padding),
    sy: sy + Math.max(0, top - padding),
    sw: Math.min(sw, right + padding) - Math.max(0, left - padding),
    sh: Math.min(sh, bottom + padding) - Math.max(0, top - padding),
  };
  spriteBoundsCache.set(id, bounds);
  return bounds;
}

function drawCardObject(
  context: CanvasRenderingContext2D,
  card: PreparedCard,
  settings: EditSettings,
  index: number,
  cardWidth: number,
  cardHeight: number,
  qualityScale: number,
) {
  const template = templateById(settings.template);
  highQuality(context);
  const x = -cardWidth / 2;
  const y = -cardHeight / 2;
  const radius = Math.max(16 * qualityScale, cardHeight * 0.13);
  const mode = settings.nameModes[index] ?? (index === 0 ? "show" : "mask");

  context.save();
  context.shadowColor = template.shadow;
  context.shadowBlur = 9 * qualityScale;
  context.shadowOffsetY = 5 * qualityScale;
  roundedPath(context, x, y, cardWidth, cardHeight, radius);
  context.fillStyle = template.surface;
  context.fill();
  context.restore();

  context.save();
  roundedPath(context, x, y, cardWidth, cardHeight, radius);
  context.clip();
  context.drawImage(card.base, x, y, cardWidth, cardHeight);
  const plateTone = context.createLinearGradient(x, y, x + cardWidth, y + cardHeight);
  plateTone.addColorStop(0, template.surface);
  plateTone.addColorStop(0.58, "rgba(255,255,255,0)");
  plateTone.addColorStop(1, card.accent);
  context.globalAlpha = 0.065;
  context.fillStyle = plateTone;
  context.fillRect(x, y, cardWidth, cardHeight);
  context.restore();

  context.save();
  context.strokeStyle = card.accent;
  context.globalAlpha = 0.32;
  context.lineWidth = Math.max(1, qualityScale * 1.35);
  roundedPath(context, x + qualityScale, y + qualityScale, cardWidth - qualityScale * 2, cardHeight - qualityScale * 2, radius);
  context.stroke();
  context.restore();

  const identity: Transform = { x: 0, y: 0, scale: 1, angle: 0, flipX: false };
  const heroAdjustment = settings.heroTransforms[index] ?? identity;
  const hero = heroGeometry(card, cardWidth, cardHeight, settings.composition, heroAdjustment);

  const drawSlot = (slot: DecorationSlot) => {
    const id = settings.decorations[slot][index] ?? "none";
    const transform = settings.decorationTransforms[slot][index] ?? identity;
    drawDecoration(context, id, card, hero, transform, cardWidth, cardHeight, slot);
  };

  context.save();
  roundedPath(context, x, y, cardWidth, cardHeight, radius);
  context.clip();
  context.translate(x, y);
  drawSlot("card");
  context.restore();

  context.save();
  context.translate(x, y);
  drawSlot("behind");
  context.restore();

  context.save();
  if (settings.composition === "flat") {
    roundedPath(context, x, y, cardWidth, cardHeight, radius);
    context.clip();
  }
  context.translate(x + hero.x + hero.width / 2, y + hero.y + hero.height);
  context.rotate(heroAdjustment.angle * Math.PI / 180);
  context.shadowColor = "rgba(29,31,38,.13)";
  context.shadowBlur = 6 * qualityScale;
  context.shadowOffsetY = qualityScale;
  context.drawImage(card.hero, -hero.width / 2, -hero.height, hero.width, hero.height);
  context.restore();

  // Names and privacy objects belong to the UI plane. Draw them after the
  // character so a moved or enlarged hero never shifts or clips the text.
  if (mode === "show") {
    context.save();
    roundedPath(context, x, y, cardWidth, cardHeight, radius);
    context.clip();
    context.drawImage(card.nameGlyphs, x, y, cardWidth, cardHeight);
    context.restore();
  } else if (mode === "mask") {
    context.save();
    context.translate(x, y);
    drawPrivacy(context, card, cardWidth, cardHeight, settings.template);
    context.restore();
  }

  context.save();
  context.translate(x, y);
  drawSlot("head");
  drawSlot("face");
  context.restore();

}

export function renderProject(
  canvas: HTMLCanvasElement,
  project: PreparedProject,
  settings: EditSettings,
  outputScale = 0.5,
) {
  const output = outputSizes[settings.ratio];
  canvas.width = Math.round(output.width * outputScale);
  canvas.height = Math.round(output.height * outputScale);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("完成画像を描画できませんでした");
  highQuality(context);
  const width = canvas.width;
  const height = canvas.height;
  const qualityScale = width / 1080;
  drawBackground(context, width, height, settings);
  const template = templateById(settings.template);
  const frame = template.frame ? resolvedAssets.get(template.frame) : null;
  if (frame) {
    context.save();
    context.globalAlpha = settings.template === "cute" ? 0.13 : 0.09;
    drawCover(context, frame, frame.naturalWidth, frame.naturalHeight, width, height);
    context.restore();
  }

  const sourceRatio = project.cards[0]?.source.width / project.cards[0]?.source.height || 3.25;
  const cardWidth = width * (project.cards.length === 3 ? 0.88 : 0.9);
  const cardHeight = cardWidth / sourceRatio;
  const gap = height * Math.max(0.002, settings.gap || template.gap);
  const totalHeight = cardHeight * project.cards.length + gap * (project.cards.length - 1);
  const centerBias = settings.ratio === "9:16" ? 0.47 : 0.5;
  const startY = height * centerBias - totalHeight / 2;

  project.cards.forEach((card, index) => {
    const angle = settings.plateAngles[index] ?? template.angles[index] ?? 0;
    const xNudge = project.cards.length === 3
      ? [-0.012, 0.011, -0.006][index] * width
      : [-0.012, 0.012][index] * width;
    const centerX = width / 2 + xNudge;
    const centerY = startY + index * (cardHeight + gap) + cardHeight / 2;
    context.save();
    context.translate(centerX, centerY);
    context.rotate(angle * Math.PI / 180);
    // Draw directly into the final canvas. The previous intermediate stage
    // resampled names and characters twice, which visibly thickened text and
    // softened character art.
    drawCardObject(context, card, settings, index, cardWidth, cardHeight, qualityScale);
    context.restore();
  });

}

export async function exportProject(project: PreparedProject, settings: EditSettings) {
  await Promise.all([
    ensureTemplateAssets(settings.template),
    ensureAccessoryAssets(selectedDecorationIds(settings)),
  ]);
  const canvas = document.createElement("canvas");
  renderProject(canvas, project, settings, 1);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("PNGを書き出せませんでした"));
    }, "image/png");
  });
}
